import React, { useState } from 'react';
import { Box, Button, Paper, Typography, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RichTextContent from '../../common/RichTextContent';
import { shuffle } from '../../../utils/shuffle';

interface MatchingPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const MatchingPlayer: React.FC<MatchingPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    const { instruction, pairs = [], learningContent = '' } = content;

    const pairKey = pairs.map((pair: any) => `${pair.id}:${pair.right}`).join('|');
    const buildOptions = () => shuffle<any>(pairs).map((pair: any) => ({ id: pair.id, text: pair.right }));

    const [options, setOptions] = useState(() => ({ key: pairKey, items: buildOptions() }));
    const [matches, setMatches] = useState<Map<string, string>>(new Map());
    const [checked, setChecked] = useState(false);

    if (options.key !== pairKey) {
        setOptions({ key: pairKey, items: buildOptions() });
        setMatches(new Map());
        setChecked(false);
    }

    const minScore = lesson.minScore || 70;

    const handleMatch = (leftId: string, rightId: string) => {
        const newMatches = new Map(matches);
        newMatches.set(leftId, rightId);
        setMatches(newMatches);
    };

    const isMatchedCorrectly = (pair: any) => matches.get(pair.id) === pair.id;

    const correctCount = pairs.filter(isMatchedCorrectly).length;
    const score = pairs.length === 0 ? 100 : Math.round((correctCount / pairs.length) * 100);
    const passed = score >= minScore;

    const handleComplete = async () => {
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    return (
        <Box>
            {learningContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <RichTextContent html={learningContent} />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {instruction || 'Ordnen Sie die passenden Begriffe einander zu'}
                </Typography>

                {pairs.map((pair: any) => {
                    const correct = isMatchedCorrectly(pair);

                    return (
                        <Box key={pair.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                            <Paper sx={{ p: 2, flex: 1 }}>
                                <Typography>{pair.left}</Typography>
                            </Paper>
                            <Typography>↔</Typography>
                            <FormControl sx={{ flex: 1 }}>
                                <InputLabel>Wählen...</InputLabel>
                                <Select
                                    value={matches.get(pair.id) || ''}
                                    label="Wählen..."
                                    onChange={(e) => handleMatch(pair.id, e.target.value)}
                                >
                                    {options.items.map((item: any) => (
                                        <MenuItem key={item.id} value={item.id}>{item.text}</MenuItem>
                                    ))}
                                </Select>
                                {checked && !correct && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                        {`Richtige Antwort: ${pair.right}`}
                                    </Typography>
                                )}
                            </FormControl>
                            {checked && (correct
                                ? <CheckCircleIcon color="success" titleAccess="Richtig zugeordnet" />
                                : <CancelIcon color="error" titleAccess="Falsch zugeordnet" />
                            )}
                        </Box>
                    );
                })}

                {checked && (
                    <Alert severity={passed ? 'success' : 'warning'} sx={{ mt: 3 }}>
                        {passed
                            ? `Ergebnis: ${score}% — bestanden.`
                            : `Ergebnis: ${score}% (mindestens ${minScore}% nötig). Grün markierte Zuordnungen stimmen, die rot markierten korrigieren Sie über die angezeigte Antwort.`}
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!checked ? (
                    <Button variant="contained" onClick={() => setChecked(true)} disabled={matches.size !== pairs.length}>
                        Absenden
                    </Button>
                ) : (
                    <Button variant="contained" onClick={handleComplete} disabled={!passed}>
                        Bestätigen
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default MatchingPlayer;
